import {
  FIND_GROUPED_QUESTIONS_BY_SECTION_USE_CASE,
  FindGroupedQuestionBySectionUseCaseOutput,
  Questions,
  Section,
  Heading,
  SectionStatus,
  HeadingStatus,
  Form,
  SectionSummaries,
} from '@module/form';
import { AttachmentFile } from '@module/ai';
import { questionItemHandlers } from './question-item';
import { sectionItemHandlers } from './section-item';
import { FormEditPageState } from '../form-edit.states';
import { Action, RequestStatus, UseCaseStateGenerator } from '@lib/plugin-redux-core';
import {
  FindGroupedQuestionsBySectionUseCaseState,
  MentionResourceState,
  OpenEditItemInFormEditUseCaseState,
  SelectItemInFormEditUseCaseState,
  UploadFilesUseCaseState,
} from './tab-questions.state';
import { OPEN_EDIT_ITEM_IN_FORM_EDIT, SELECT_ITEM_IN_FORM_EDIT } from 'redux/root.action';
import { headingItemHandlers } from './heading-item';
import {
  ADD_MENTION_RESOURCE,
  CLEAR_MENTION_RESOURCES,
  REMOVE_MENTION_RESOURCE,
  UPDATE_AI_CHANGE_RESOURCE_DATA,
  REMOVE_AI_CHANGE_ITEM,
  UPDATE_AI_PROCESSING_STATE,
  REJECT_ALL_AI_CHANGES,
  ACCEPT_ALL_AI_CHANGES,
  UPLOAD_FILES,
  SNAPSHOT_CURRENT_MENTION_RESOURCES,
} from './tab-questions-action';
import { AiChangeState } from './tab-questions.state';
import { Question, QuestionProps } from '@module/form';
import {
  AiChangeResourceType,
  LOAD_RESOURCE_CHANGES_USE_CASE,
  AIResourceChangeRecord,
} from '@module/ai';
import { AiJsonToEntityConverter } from '../../../services';

const getFormTotals = (questions: Question<QuestionProps>[] = []) => ({
  formTotalQuestions: Questions.getTotalQuestions(questions),
  formTotalScore: Questions.getTotalScore(questions),
});

export const handleFindGroupedQuestionsBySection: UseCaseStateGenerator<FindGroupedQuestionsBySectionUseCaseState> =
  {
    name: FIND_GROUPED_QUESTIONS_BY_SECTION_USE_CASE,
    executing: (state: FormEditPageState): FormEditPageState => {
      return {
        ...state,
        isLoadingFindGroupedQuestionsBySectionState: true,
        isLoadingGetQuestions: true,
      };
    },
    success: (state: FormEditPageState, action: Action): FormEditPageState => {
      const { payload } = action;
      const output = payload as FindGroupedQuestionBySectionUseCaseOutput;
      const { sections } = state;

      const { sectionSummaries, questions } = output;
      const sortedQuestions = Questions.sortQuestionsWithIndexAndSection(questions);

      const hasEssayOrFillInBlankQuestion =
        Questions.hasEssayOrFillInBlankQuestion(sortedQuestions);
      let selectedSection: Section;
      let selectedFormItemId: string;
      if (sections && sections.length > 0) {
        selectedSection = sections[0];
        selectedFormItemId = sections[0].id.toString();
      }

      return {
        ...state,
        questions,
        ...getFormTotals(sortedQuestions),
        selectedSection,
        selectedFormItemId,
        sectionSummaries,
        isQuestionEditing: false,
        isFormHasQuestions:
          (sections?.length ?? 0) > 0 &&
          (questions || []).some((q) => {
            const status = (q as any).status;
            return (
              status && status !== 'New' && status !== 'ChangeNewType' && status !== 'Deactivate'
            );
          }),
        hasEssayOrFillInBlankQuestion,
        isLoadingGetQuestions: false,
      };
    },
    error: (state: FormEditPageState, action: Action): FormEditPageState => {
      const { error } = action;
      return {
        ...state,
        error,
        isLoadingGetQuestions: false,
      };
    },
  };

export const handleSelectItemInFormEditUseCase: UseCaseStateGenerator<SelectItemInFormEditUseCaseState> =
  {
    name: SELECT_ITEM_IN_FORM_EDIT,
    executing: (state: FormEditPageState, action: Action): FormEditPageState => {
      const selectedFormItemId = action.payload;
      return { ...state, selectedFormItemId };
    },
    reset: (state: FormEditPageState): FormEditPageState => {
      return { ...state, selectedFormItemId: '' };
    },
  };

export const handleOpenEditItemInFormEditUseCase: UseCaseStateGenerator<OpenEditItemInFormEditUseCaseState> =
  {
    name: OPEN_EDIT_ITEM_IN_FORM_EDIT,
    executing: (state: FormEditPageState, action: Action): FormEditPageState => {
      const editingFormItemId = action.payload;
      return { ...state, editingFormItemId };
    },
    reset: (state: FormEditPageState): FormEditPageState => {
      return { ...state, editingFormItemId: '' };
    },
  };

export const handleAddMentionResource: UseCaseStateGenerator<MentionResourceState> = {
  name: ADD_MENTION_RESOURCE,
  executing: (state: FormEditPageState, action: Action): FormEditPageState => {
    const mentionResource = action.payload;

    let mentionResources = state.mentionResources ?? [];

    const foundMentionResource = mentionResources.find(
      (resource) => resource.data.id.toString() === mentionResource.data.id.toString(),
    );
    if (!foundMentionResource) {
      mentionResources.push(mentionResource);
    }

    return { ...state, mentionResources };
  },
};

export const handleRemoveMentionResource: UseCaseStateGenerator<MentionResourceState> = {
  name: REMOVE_MENTION_RESOURCE,
  executing: (state: FormEditPageState, action: Action): FormEditPageState => {
    const removedItem = state.mentionResources?.filter(
      (resource) => resource.data.id.toString() !== action.payload,
    );

    return { ...state, mentionResources: removedItem };
  },
};

export const handleClearMentionResources: UseCaseStateGenerator<MentionResourceState> = {
  name: CLEAR_MENTION_RESOURCES,
  executing: (state: FormEditPageState): FormEditPageState => {
    return { ...state, mentionResources: [] };
  },
};

export const handleSnapshotCurrentMentionResources: UseCaseStateGenerator<MentionResourceState> = {
  name: SNAPSHOT_CURRENT_MENTION_RESOURCES,
  executing: (state: FormEditPageState): FormEditPageState => {
    return { ...state, snapshotMentionResources: state.mentionResources };
  },
};

export const handleUpdateAiProcessingState: UseCaseStateGenerator<AiChangeState> = {
  name: UPDATE_AI_PROCESSING_STATE,
  executing: (state: FormEditPageState, action: Action): FormEditPageState => {
    const isProcessing = action.payload as boolean;
    return { ...state, aiProcessing: isProcessing };
  },
};

export const handleRejectAllAiChanges: UseCaseStateGenerator<AiChangeState> = {
  name: REJECT_ALL_AI_CHANGES,
  executing: (state: FormEditPageState): FormEditPageState => {
    return {
      ...state,
      hasAiChanges: false,
      mentionResources: [],
      aiChanges: [],
      sections: [],
      headings: [],
      questions: [],
      selectedFormItemId: null,
      sectionSummaries: SectionSummaries.toSectionSummaries([]),
      ...getFormTotals([]),
    };
  },
};

export const handleAcceptAllAiChanges: UseCaseStateGenerator<AiChangeState> = {
  name: ACCEPT_ALL_AI_CHANGES,
  executing: (state: FormEditPageState): FormEditPageState => {
    const activatedSections = (state.sections || []).map((s) => {
      const cloned = Section.cloneTo(s);
      cloned.updateStatus(SectionStatus.Activate);
      return cloned;
    });

    const activatedQuestions = (state.questions || []).map((q) => {
      const cloned = q.clone();
      cloned.activate();
      return cloned;
    });

    const activatedHeadings = (state.headings || []).map((h) => {
      const cloned = h.clone();
      cloned.updateStatus(HeadingStatus.Update);
      return cloned;
    });

    return {
      ...state,
      sections: activatedSections,
      questions: activatedQuestions,
      headings: activatedHeadings,
      aiChanges: [],
      hasAiChanges: false,
      mentionResources: [],
      aiAcceptAllChangeStatus: RequestStatus.EXECUTE,
      isFormHasQuestions:
        activatedSections.length > 0 &&
        activatedQuestions.some((q) => {
          const status = (q as any).status;
          return (
            status && status !== 'New' && status !== 'ChangeNewType' && status !== 'Deactivate'
          );
        }),
      sectionSummaries: SectionSummaries.toSectionSummaries(activatedSections),
      ...getFormTotals(activatedQuestions),
    };
  },
};

export interface UpdateAiChangeResourceDataPayload {
  changedForm?: Form;
  changedQuestions?: Question<QuestionProps>[];
}
export const handleUpdateAiChangeResourceData: UseCaseStateGenerator<AiChangeState> = {
  name: UPDATE_AI_CHANGE_RESOURCE_DATA,

  executing: (state: FormEditPageState, action: Action): FormEditPageState => {
    const { changedForm, changedQuestions } = action.payload as UpdateAiChangeResourceDataPayload;

    return {
      ...state,
      questions: changedQuestions,
      sections: changedForm.sections,
      headings: changedForm.headings,
      ...getFormTotals(changedQuestions || []),
      hasAiChanges:
        changedForm.sections.length > 0 ||
        changedQuestions.length > 0 ||
        changedForm.headings.length > 0,
      updateAiChangeStatus: RequestStatus.SUCCESS,
    };
  },

  reset: (state: FormEditPageState): FormEditPageState => {
    return { ...state, updateAiChangeStatus: RequestStatus.RESET };
  },
};

export const handleRemoveAiChangeItem: UseCaseStateGenerator<AiChangeState> = {
  name: REMOVE_AI_CHANGE_ITEM,
  executing: (state: FormEditPageState, action: Action): FormEditPageState => {
    const id = action.payload as string;
    const aiChanges = (state.aiChanges ?? []).filter((item) => item.id !== id);
    return { ...state, aiChanges };
  },
};

export const handleLoadResourceChangesUseCase: UseCaseStateGenerator<AiChangeState> = {
  name: LOAD_RESOURCE_CHANGES_USE_CASE,
  executing: (state: FormEditPageState): FormEditPageState => ({
    ...state,
    getAiChangeResourcesStatus: RequestStatus.EXECUTE,
  }),
  success: (state: FormEditPageState, action: Action): FormEditPageState => {
    const records = (action.payload || []) as AIResourceChangeRecord[];

    if (records.length === 0) {
      return {
        ...state,
        hasAiChanges: false,
        getAiChangeResourcesStatus: RequestStatus.SUCCESS,
      };
    }

    const questions = records
      .filter((r) => r.itemType === AiChangeResourceType.Question)
      .map(
        (r) =>
          AiJsonToEntityConverter.execute({
            itemType: AiChangeResourceType.Question,
            json: r.after,
          }) as Question<QuestionProps>,
      );

    const sections = records
      .filter((r) => r.itemType === AiChangeResourceType.Section)
      .map(
        (r) =>
          AiJsonToEntityConverter.execute({
            itemType: AiChangeResourceType.Section,
            json: r.after,
          }) as Section,
      );

    const headings = records
      .filter((r) => r.itemType === AiChangeResourceType.Heading)
      .map(
        (r) =>
          AiJsonToEntityConverter.execute({
            itemType: AiChangeResourceType.Heading,
            json: r.after,
          }) as Heading,
      );

    return {
      ...state,
      questions,
      ...getFormTotals(questions),
      sections,
      headings,
      hasAiChanges: true,
      getAiChangeResourcesStatus: RequestStatus.SUCCESS,
    };
  },
};

const handleUploadFiles: UseCaseStateGenerator<UploadFilesUseCaseState> = {
  name: UPLOAD_FILES,
  executing: (state: FormEditPageState): FormEditPageState => {
    return {
      ...state,
      isUploadingFiles: true,
      uploadFilesError: undefined,
    };
  },
  success: (state: FormEditPageState, action: Action): FormEditPageState => {
    const uploadedFiles = action.payload as AttachmentFile[];
    return {
      ...state,
      isUploadingFiles: false,
      uploadedFiles,
    };
  },
  error: (state: FormEditPageState, action: Action): FormEditPageState => {
    return {
      ...state,
      isUploadingFiles: false,
      uploadFilesError: action.error as unknown as string,
    };
  },
};

export const tabQuestionsHandlers = [
  handleFindGroupedQuestionsBySection,
  handleSelectItemInFormEditUseCase,
  handleOpenEditItemInFormEditUseCase,
  handleAddMentionResource,
  handleRemoveMentionResource,
  handleClearMentionResources,
  handleUpdateAiChangeResourceData,
  handleUpdateAiProcessingState,
  handleRemoveAiChangeItem,
  handleRejectAllAiChanges,
  handleAcceptAllAiChanges,
  handleLoadResourceChangesUseCase,
  handleUploadFiles,
  handleSnapshotCurrentMentionResources,
  ...questionItemHandlers,
  ...sectionItemHandlers,
  ...headingItemHandlers,
];
